import { CheckCircleOutlined, InfoCircleOutlined, PlusOutlined } from '@ant-design/icons';
import { Button, Flex, Modal, Space, Typography } from 'antd';
import { useIntl } from 'react-intl';
import { messages } from '@/features/scan/messages';
import type { CaptureSession } from '@/shared/capture/capture-session';
import type { MeterSearchResult, NormalizedMeterIdentifier } from '@/shared/meter-identification';

export type PendingScanMatch =
  | {
      identifier: NormalizedMeterIdentifier;
      meter: MeterSearchResult;
      type: 'cached';
    }
  | {
      identifier: NormalizedMeterIdentifier;
      session: CaptureSession;
      type: 'queued';
    };

interface ScanSearchOutcomeProps {
  hasSearched: boolean;
  identifier: NormalizedMeterIdentifier | null;
  isSearching: boolean;
  onCreate: () => void;
  onSkip: () => void;
  onUpdate: () => void;
  queuedMatches: CaptureSession[];
  results: MeterSearchResult[];
}

export function ScanSearchOutcome({
  hasSearched,
  identifier,
  isSearching,
  onCreate,
  onSkip,
  onUpdate,
  queuedMatches,
  results,
}: ScanSearchOutcomeProps) {
  const intl = useIntl();
  if (isSearching) {
    return (
      <Flex align="center" gap="small" justify="space-between" wrap>
        <Typography.Text type="secondary">{intl.formatMessage(messages.scanSearching)}</Typography.Text>
        <Button icon={<PlusOutlined />} onClick={onSkip}>
          {intl.formatMessage(messages.scanSkip)}
        </Button>
      </Flex>
    );
  }

  if (!hasSearched) {
    return (
      <Flex align="center" gap="small" justify="space-between" wrap>
        <Typography.Text type="secondary">{intl.formatMessage(messages.scanScanOrType)}</Typography.Text>
        <Button icon={<PlusOutlined />} onClick={onSkip}>
          {intl.formatMessage(messages.scanSkip)}
        </Button>
      </Flex>
    );
  }

  if (queuedMatches.length > 0 || results.length > 0) {
    return (
      <Flex align="center" gap="middle" justify="space-between" wrap>
        <Space size="small">
          <CheckCircleOutlined style={{ color: '#52c41a' }} />
          <Typography.Text strong>{intl.formatMessage(messages.scanExistingMeterFound)}</Typography.Text>
        </Space>
        <Button onClick={onUpdate} type="primary">
          {intl.formatMessage(messages.scanUpdate)}
        </Button>
      </Flex>
    );
  }

  return (
    <Flex align="center" gap="middle" justify="space-between" wrap>
      <Space size="small">
        <InfoCircleOutlined style={{ color: '#1677ff' }} />
        <Typography.Text strong>{intl.formatMessage(messages.scanNoMeterFound)}</Typography.Text>
      </Space>
      <Button disabled={!identifier?.normalizedValue} onClick={onCreate} type="primary">
        {intl.formatMessage(messages.scanCreate)}
      </Button>
    </Flex>
  );
}

export function ScanMatchModal({
  match,
  onCancel,
  onConfirm,
  onRunPassport,
}: {
  match: PendingScanMatch | null;
  onCancel: () => void;
  onConfirm: (match: PendingScanMatch) => void;
  onRunPassport: (match: Extract<PendingScanMatch, { type: 'cached' }>) => void;
}) {
  const intl = useIntl();
  const footer = match ? (
    <Flex gap="small" justify="flex-end" wrap>
      <Button onClick={onCancel}>{intl.formatMessage({ id: 'app.common.cancel' })}</Button>
      {match.type === 'cached' ? (
        <Button onClick={() => onRunPassport(match)}>{intl.formatMessage(messages.scanRunPassport)}</Button>
      ) : null}
      <Button onClick={() => onConfirm(match)} type="primary">
        {match.type === 'queued'
          ? intl.formatMessage(messages.scanOpenQueuedMeter)
          : intl.formatMessage(messages.scanUpdateMeter)}
      </Button>
    </Flex>
  ) : null;

  return (
    <Modal
      footer={footer}
      onCancel={onCancel}
      open={!!match}
      title={intl.formatMessage(messages.scanExistingMeterFoundTitle)}
    >
      <Typography.Paragraph>
        {match?.type === 'queued'
          ? intl.formatMessage(messages.scanQueuedMatchDescription)
          : intl.formatMessage(messages.scanCachedMatchDescription)}
      </Typography.Paragraph>
      {match ? <MatchSummary match={match} /> : null}
    </Modal>
  );
}

function MatchSummary({ match }: { match: PendingScanMatch }) {
  const intl = useIntl();
  if (match.type === 'queued') {
    return (
      <Space direction="vertical" size={2}>
        <Typography.Text strong>
          {intl.formatMessage(messages.scanSessionSummary, {
            id: match.session.id.slice(0, 14),
          })}
        </Typography.Text>
        <Typography.Text type="secondary">
          {intl.formatMessage(messages.scanMatchedBy, {
            field: match.identifier.kind,
          })}
        </Typography.Text>
      </Space>
    );
  }

  return (
    <Space direction="vertical" size={2}>
      <Typography.Text strong>{match.meter.data.serial ?? match.meter.data.apiKey ?? match.meter.id}</Typography.Text>
      <Typography.Text type="secondary">
        {intl.formatMessage(messages.scanMeterTypeMatchedBy, {
          field: match.meter.matchedBy,
          meterType: match.meter.data.commodity ?? '',
        })}
      </Typography.Text>
    </Space>
  );
}
